import {
  AnnotationShape,
  MirrorShape,
} from '@/web-components/import-wizard-v2/typings/common';
import { useStore as useProductStore } from './product';
import { useStore as useShapeStore } from './shape';

import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { PositionMirrorSettings } from '@/web-components/import-wizard/typings/common/mirror';
import {
  LanyardConfiguration as ProofLanyardCOnfiguration,
  Category,
  LanyardPrintPositionConfiguration,
} from '@midocean/webapps-shared-domain';
import { mm2px } from '@/utils';
import { Proof } from '@midocean/webapps-shared-domain';
import { MirrorSnapshot } from '@/typings/history';

export const useStore = defineStore({
  id: 'mirror',
  state: () => {
    return {
      mirrorShapes: [] as MirrorShape[],
      positionMirrorShapeMap: [] as Record<string, string>[],
      positionMirrorSettings: [] as PositionMirrorSettings[],
    };
  },
  getters: {
    isMirrorEffectAppliedToShape: (state) => {
      return (shapeId: string): boolean => {
        return (
          state.mirrorShapes.findIndex((x) => x.originalShapeId === shapeId) !==
          -1
        );
      };
    },
    currentMirrorSettings: (state) => {
      const productStore = useProductStore();
      return state.positionMirrorSettings.find(
        (x) => x.positionId === productStore.selectedPrintPosition.id
      );
    },
  },
  actions: {
    getActiveMirrorShapes(): MirrorShape[] {
      const productStore = useProductStore();
      return this.mirrorShapes.length > 0
        ? [
            ...this.mirrorShapes.filter((shape) =>
              this.positionMirrorShapeMap.some(
                (psm) =>
                  psm.positionId === productStore.selectedPrintPosition.id &&
                  psm.mirrorId === shape.id
              )
            ),
          ]
        : [];
    },
    reassignMirrorShapes({ from, to }: { from: string; to: string }) {
      if (from === to) {
        return;
      }

      const productStore = useProductStore();

      const productPrintPosition = productStore.data.productPrintPositions.find(
        (x) => x.id === to
      );
      if (
        productPrintPosition &&
        productPrintPosition.category === Category.Lanyard
      ) {
        const reassignedFromTo = this.positionMirrorShapeMap.map((psm) => {
          return psm.positionId === from ? { ...psm, positionId: to } : psm;
        });
        this.positionMirrorShapeMap = reassignedFromTo;
      } else {
        this.mirrorShapes = [];
        this.positionMirrorSettings = [];
      }
    },
    initializeMirrorSetting() {
      const productStore = useProductStore();
      productStore.unavailablePrintPositions.forEach((p) => {
        const productPrintPosition =
          productStore.data.productPrintPositions.find(
            (x) => x.name === p.printPositionSAPCode
          );
        if (
          productPrintPosition &&
          productPrintPosition.category === Category.Lanyard
        ) {
          const configuration =
            productPrintPosition.categoryConfiguration as LanyardPrintPositionConfiguration;
          this.positionMirrorSettings.push({
            positionId: p.positionId,
            isMirrorApplied: false,
            isOppositeRotationApplied: false,
            distance: mm2px(
              configuration.distanceOfMirrorPosition,
              productPrintPosition.originalImageWebDpi
            ),
          });
        }
      });
    },

    initializeMirrorSettingsFromProof(proof: Proof) {
      const productStore = useProductStore();
      productStore.unavailablePrintPositions.forEach((p) => {
        const proofPrintPosition = proof.productPrintPositions.find(
          (x) => x.name === p.printPositionSAPCode
        );
        if (
          proofPrintPosition &&
          proofPrintPosition.category === Category.Lanyard
        ) {
          const productPrintPosition =
            productStore.data.productPrintPositions.find(
              (x) => x.name === proofPrintPosition.name
            );

          if (
            productPrintPosition &&
            productPrintPosition.category === Category.Lanyard
          ) {
            const configuration =
              productPrintPosition.categoryConfiguration as LanyardPrintPositionConfiguration;

            const proofConfiguration =
              proofPrintPosition.categoryConfiguration as ProofLanyardCOnfiguration;
            this.positionMirrorSettings.push({
              positionId: p.positionId,
              isMirrorApplied: proofConfiguration.isMirrorApplied,
              isOppositeRotationApplied:
                proofConfiguration.isOppositeRotationApplied,
              distance: mm2px(
                configuration.distanceOfMirrorPosition,
                productPrintPosition.originalImageWebDpi
              ),
            });
          }
        }
      });

      this.initializeMirrorPositions();
    },

    addPositionMirrorSettings(
      positionId: string,
      isMirrorApplied: boolean,
      isOppositeRotationApplied: boolean
    ) {
      const productStore = useProductStore();

      const productPrintPosition = productStore.data.productPrintPositions.find(
        (x) => x.id === positionId
      );
      if (
        productPrintPosition &&
        productPrintPosition.category === Category.Lanyard
      ) {
        const configuration =
          productPrintPosition.categoryConfiguration as LanyardPrintPositionConfiguration;
        this.positionMirrorSettings.push({
          positionId: positionId,
          isMirrorApplied: isMirrorApplied,
          isOppositeRotationApplied: isOppositeRotationApplied,
          distance: mm2px(
            configuration.distanceOfMirrorPosition,
            productPrintPosition.originalImageWebDpi
          ),
        });
      }
    },
    removePositionMirrorSettings(positionId: string) {
      const idx = this.positionMirrorSettings.findIndex(
        (x) => x.positionId === positionId
      );
      if (idx !== -1) {
        this.positionMirrorSettings.splice(idx, 1);
        this.positionMirrorShapeMap = this.positionMirrorShapeMap.filter(
          (x) => x.positionId !== positionId
        );
      }
    },
    addMirrorShape(originalShape: AnnotationShape) {
      const productStore = useProductStore();
      if (this.currentMirrorSettings) {
        const rotation = this.currentMirrorSettings.isOppositeRotationApplied
          ? (originalShape.rotation + 180) % 360
          : originalShape.rotation;

        const mirroredShape: MirrorShape = {
          ...originalShape,
          originalShapeId: originalShape.id,
          id: uuidv4(),
          y: originalShape.y + this.currentMirrorSettings.distance,
          rotation: rotation * (Math.PI / 180),
          disableManipulate: false,
          disableSelect: true,
          isSelected: false,
        };

        this.mirrorShapes.push(mirroredShape);

        this.positionMirrorShapeMap.push({
          positionId: productStore.selectedPrintPosition.id,
          mirrorId: mirroredShape.id,
        });
      }
    },

    addMirrorShapeToPosition(
      originalShape: AnnotationShape,
      positionId: string
    ) {
      const mirrorSettings = this.positionMirrorSettings.find(
        (x) => x.positionId === positionId
      );
      if (mirrorSettings) {
        const rotation = mirrorSettings.isOppositeRotationApplied
          ? (originalShape.rotation + 180) % 360
          : originalShape.rotation;

        const mirroredShape: MirrorShape = {
          ...originalShape,
          originalShapeId: originalShape.id,
          id: uuidv4(),
          y: originalShape.y + mirrorSettings.distance,
          rotation: rotation * (Math.PI / 180),
          disableManipulate: false,
          disableSelect: true,
          isSelected: false,
        };

        this.mirrorShapes.push(mirroredShape);

        this.positionMirrorShapeMap.push({
          positionId: mirrorSettings.positionId,
          mirrorId: mirroredShape.id,
        });
      }
    },
    removeMirrorShape(originalShapeId: string) {
      const idx = this.mirrorShapes.findIndex(
        (x) => x.originalShapeId === originalShapeId
      );
      if (idx !== -1) {
        this.mirrorShapes.splice(idx, 1);
      }
    },
    updateMirrorShape(originalShape: AnnotationShape) {
      const mirrorShapeIdx = this.mirrorShapes.findIndex(
        (x) => x.originalShapeId === originalShape.id
      );

      if (mirrorShapeIdx !== -1 && this.currentMirrorSettings) {
        const mirrorShape = this.mirrorShapes[mirrorShapeIdx];

        if (mirrorShape.kind === 'Logo' && originalShape.kind === 'Logo') {
          mirrorShape.backgroundImage = originalShape.backgroundImage;
        }

        if (mirrorShape.kind === 'Text' && originalShape.kind === 'Text') {
          mirrorShape.w2pText.color = originalShape.w2pText.color;
          mirrorShape.w2pText.text = originalShape.w2pText.text;
          mirrorShape.w2pText.fontSize = originalShape.w2pText.fontSize;
          mirrorShape.w2pText.fontWeight = originalShape.w2pText.fontWeight;
          mirrorShape.w2pText.fontStyle = originalShape.w2pText.fontStyle;
          mirrorShape.w2pText.textAlign = originalShape.w2pText.textAlign;
          mirrorShape.w2pText.fontFamily = originalShape.w2pText.fontFamily;
        }

        this.mirrorShapes.splice(mirrorShapeIdx, 1, {
          ...mirrorShape,
          x: originalShape.x,
          y: originalShape.y + this.currentMirrorSettings.distance,
          width: originalShape.width,
          height: originalShape.height,
        });
      }
    },
    updateRotation(isOppositeRotationApplied: boolean) {
      const shapeStore = useShapeStore();
      const productStore = useProductStore();
      const settingsIdx = this.positionMirrorSettings.findIndex(
        (x) => x.positionId === productStore.selectedPrintPosition.id
      );
      if (this.currentMirrorSettings) {
        this.positionMirrorSettings.splice(settingsIdx, 1, {
          ...this.currentMirrorSettings,
          isOppositeRotationApplied,
        });

        this.mirrorShapes.forEach((mirrorShape, idx) => {
          const originalShape = shapeStore.shapes.find(
            (s) => s.id === mirrorShape.originalShapeId
          );
          if (originalShape) {
            const rotation = isOppositeRotationApplied
              ? (originalShape.rotation + 180) % 360
              : originalShape.rotation;

            this.mirrorShapes.splice(idx, 1, {
              ...mirrorShape,
              rotation: rotation * (Math.PI / 180),
            });
          }
        });
      }
    },
    updateMirror(isMirrorApplied: boolean) {
      const productStore = useProductStore();
      const settingsIdx = this.positionMirrorSettings.findIndex(
        (x) => x.positionId === productStore.selectedPrintPosition.id
      );
      if (this.currentMirrorSettings) {
        this.positionMirrorSettings.splice(settingsIdx, 1, {
          ...this.currentMirrorSettings,
          isMirrorApplied,
        });
        if (!isMirrorApplied) {
          this.mirrorShapes = [];
          this.positionMirrorShapeMap = [];
        }

        if (isMirrorApplied) {
          const productStore = useProductStore();
          const shapeStore = useShapeStore();
          const originalShapes = shapeStore.shapes.filter((shape) =>
            shapeStore.positionShapeMap.some(
              (psm) =>
                psm.id === productStore.selectedPrintPosition.id &&
                psm.logoId === shape.id
            )
          );

          originalShapes.forEach((shape) => this.addMirrorShape(shape));
        }
      }
    },
    initializeMirrorPositions() {
      const shapeStore = useShapeStore();
      const productStore = useProductStore();

      for (const position of productStore.unavailablePrintPositions) {
        const mirrorPostionSettingsIdx = this.positionMirrorSettings.findIndex(
          (x) => x.positionId === position.positionId
        );

        if (mirrorPostionSettingsIdx !== -1) {
          const isMirrorApplied =
            this.positionMirrorSettings[mirrorPostionSettingsIdx]
              .isMirrorApplied;

          if (isMirrorApplied) {
            const positionOriginalShapesIds =
              shapeStore.positionShapeMap.filter(
                (x) => x.id === position.positionId
              );

            for (const positionShapeID of positionOriginalShapesIds) {
              const originalShape = shapeStore.shapes.find(
                (x) => x.id === positionShapeID.logoId
              );
              if (originalShape) {
                this.addMirrorShapeToPosition(
                  originalShape,
                  position.positionId
                );
              }
            }
          }
        }
      }
    },

    updateMirrorStateFromSnapshot(snapshot: MirrorSnapshot) {
      this.$patch((state) => {
        if (snapshot !== undefined) {
          // TODO - fix when types are aligned
          //state.mirrorShapes = snapshot.mirrorShapes;
          state.positionMirrorSettings = snapshot.positionMirrorSettings;
          state.positionMirrorShapeMap = snapshot.positionMirrorShapeMap;
        }
      });
    },
  },
});
